import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Leaf, Heart, Sun, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_beauty-bazaar-117/artifacts/2j1k3po7_Reggae%20Essences%20Logo.png";

export const AboutPage = () => {
  return (
    <div data-testid="about-page" className="min-h-screen pt-28 pb-16 bg-cream">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 mb-20">
        <div className="text-center">
          <span className="text-terracotta font-mono text-sm uppercase tracking-wider">
            Our Story
          </span>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-forest mt-2 mb-6">
            About Reggae Essences
          </h1>
          <p className="text-warm-brown text-lg max-w-2xl mx-auto">
            Naturally rooted. Soulfully made. Bringing peaceful island energy to your self-care routine.
          </p>
        </div>
      </section>

      {/* Main Story */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 mb-20">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div className="order-2 lg:order-1">
            <h2 className="font-heading text-3xl font-bold text-forest mb-6">
              Handmade Care with Island Rhythm
            </h2>
            <div className="space-y-4 text-warm-brown leading-relaxed">
              <p>
                Reggae Essences was born from a deep love of natural self-care and the 
                soulful rhythm of island living. What started as a personal journey to 
                find gentle, nourishing products for sensitive skin blossomed into a 
                passion for creating handmade treasures that everyone can enjoy.
              </p>
              <p>
                Every bar of soap, every jar of body butter, and every candle is crafted 
                in small batches with intention and love. We believe that self-care should 
                feel like a retreat — calm, grounded, and full of good vibes.
              </p>
              <p>
                Our products are inspired by the warmth of Caribbean culture, the richness 
                of natural ingredients, and the belief that taking care of yourself should 
                be a joyful, soulful experience.
              </p>
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <img
              src={LOGO_URL}
              alt="Reggae Essences"
              className="w-full max-w-md mx-auto rounded-3xl shadow-2xl"
            />
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-forest">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-gold font-mono text-sm uppercase tracking-wider">
              What We Stand For
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-cream mt-2">
              Our Values
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Leaf,
                title: 'Natural',
                description: 'We use only high-quality, skin-loving ingredients. No harsh chemicals, ever.',
              },
              {
                icon: Heart,
                title: 'Handmade',
                description: 'Every product is lovingly crafted in small batches with care and intention.',
              },
              {
                icon: Sun,
                title: 'Island-Inspired',
                description: 'Our scents and formulas capture the warmth and soul of Caribbean living.',
              },
              {
                icon: Sparkles,
                title: 'Luxurious',
                description: 'Simple self-care elevated to a soulful, pampering experience.',
              },
            ].map((value, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-gold/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <value.icon className="h-8 w-8 text-gold" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-cream mb-2">
                  {value.title}
                </h3>
                <p className="text-cream/70">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-20 bg-sand/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-terracotta font-mono text-sm uppercase tracking-wider">
              Our Products
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-forest mt-2">
              What We Create
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-cream p-8 rounded-2xl text-center">
              <img
                src="https://customer-assets.emergentagent.com/job_beauty-bazaar-117/artifacts/vcv7ymch_Screenshot_20260206_130334_Facebook.jpg"
                alt="Handmade Soaps"
                className="w-full h-48 object-cover rounded-xl mb-6"
              />
              <h3 className="font-heading text-xl font-semibold text-forest mb-2">
                Handmade Bar Soaps
              </h3>
              <p className="text-warm-brown">
                Gently cleanse while leaving your skin soft, refreshed, and naturally nourished.
              </p>
            </div>

            <div className="bg-cream p-8 rounded-2xl text-center">
              <img
                src="https://images.pexels.com/photos/4202465/pexels-photo-4202465.jpeg"
                alt="Body Butters"
                className="w-full h-48 object-cover rounded-xl mb-6"
              />
              <h3 className="font-heading text-xl font-semibold text-forest mb-2">
                Whipped Body Butters
              </h3>
              <p className="text-warm-brown">
                Deeply moisturizing and perfect for everyday glow and long-lasting hydration.
              </p>
            </div>

            <div className="bg-cream p-8 rounded-2xl text-center">
              <img
                src="https://images.pexels.com/photos/5775023/pexels-photo-5775023.jpeg"
                alt="Candles"
                className="w-full h-48 object-cover rounded-xl mb-6"
              />
              <h3 className="font-heading text-xl font-semibold text-forest mb-2">
                Hand-Poured Candles
              </h3>
              <p className="text-warm-brown">
                Clean-burning and thoughtfully scented to bring warmth and comfort to any space.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Promise */}
      <section className="py-20 bg-cream">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-forest mb-6">
            Our Promise to You
          </h2>
          <div className="space-y-4 text-warm-brown text-lg leading-relaxed">
            <p>
              When you shop with Reggae Essences, you're not just buying products — 
              you're supporting handmade, heart-driven creations designed to make 
              everyday moments feel a little more special.
            </p>
            <p>
              We promise to always use natural, skin-loving ingredients. We promise 
              to craft each product with care and intention. And we promise to bring 
              you a little piece of island peace with every purchase.
            </p>
          </div>
          <Link to="/shop">
            <Button
              className="mt-8 bg-forest hover:bg-forest-light text-cream px-8 py-4 rounded-full"
              data-testid="shop-now-about"
            >
              Explore Our Products
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Brand Personality */}
      <section className="py-20 bg-gold/10">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-forest mb-4">
            Our Vibe
          </h2>
          <p className="font-heading text-xl md:text-2xl text-warm-brown italic">
            "Calm, confident, warm, and grounded — bringing peaceful island energy to your self-care routine."
          </p>
        </div>
      </section>
    </div>
  );
};
